import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Autocomplete, CircularProgress, Chip,
} from '@mui/material'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { toast } from 'react-toastify'
import {
  convertActionToTaskRoutine,
  selectMutationLoading,
} from 'features/actions/ducks'
import { searchAssignableUsers } from 'services/taskService'
import FormTemplateField from 'features/taskManagement/components/FormTemplateField'
import { requestTaskManagementFeature } from 'services/BpService'
import { fetchBpUnits } from 'services/unitsService'
import { listContractors } from 'services/contractorService'
import { fetchTaskDomains } from 'services/ProgrammeService'
import useDebounce from 'utils/hooks/useDebounce'
import { severityMeta } from 'features/actions/workspace/remedialMeta'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'
import { promisifyRoutine } from 'redux-saga-routines'
import SubmitError from './SubmitError'
import { firstErrorMessage } from 'features/actions/ducks/messages'
import { ROLE_LABELS } from 'utils/roles'

const convertAsync = promisifyRoutine(convertActionToTaskRoutine)

const PRIORITIES = [
  { value: 'critical', label: PRIORITY_META.critical.label },
  { value: 'high',   label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low',    label: 'Low' },
]

// Mirrors mapSeverityToPriority on the API
// (api/app/Http/Actions/Actions/ConvertActionToTask.php)
const SEVERITY_TO_PRIORITY = {
  critical: 'critical',
  high:     'high',
  medium:   'medium',
  low:      'low',
}

const userLabel = (u) => {
  if (!u) return ''
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name || u.email || u.id
}

const dialogPaperSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
}

const dialogTitleSx = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#0C1229',
  padding: '20px 24px',
  borderBottom: '1px solid #f2f2f2',
}

const dialogContentSx = {
  padding: '20px 24px !important',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const dialogActionsSx = {
  padding: '16px 24px',
  borderTop: '1px solid #f2f2f2',
  gap: '8px',
}

const cancelButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  color: '#0C1229',
  borderColor: '#f2f2f2',
  borderRadius: '8px',
  padding: '8px 20px',
  '&:hover': {
    backgroundColor: '#f2f2f2',
    borderColor: '#0C1229',
  },
}

const submitButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#0C1229',
  borderRadius: '8px',
  padding: '8px 24px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#D24724',
    boxShadow: '0 4px 12px rgba(210, 71, 36, 0.3)',
  },
}

const ConvertToTaskDialog = ({ action, open, onClose, onConverted, zIndex }) => {
  const dispatch = useDispatch()
  const submitting = useSelector(selectMutationLoading)
  const [submitError, setSubmitError] = useState(null)
  useEffect(() => { if (open) setSubmitError(null) }, [open])
  const [title, setTitle] = useState('')
  const [deadline, setDeadline] = useState('')
  const [priority, setPriority] = useState('medium')
  const [assignees, setAssignees] = useState([])
  const [formTemplate, setFormTemplate] = useState(null)
  const [poNumber, setPoNumber] = useState('')

  const [userQuery, setUserQuery] = useState('')
  const debouncedUserQuery = useDebounce(userQuery, 250)
  const [userOptions, setUserOptions] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)


  const [domains, setDomains] = useState([])
  const [taskDomainId, setTaskDomainId] = useState('')

  const [recurrenceType, setRecurrenceType] = useState('one_off')
  const [recurrenceInterval, setRecurrenceInterval] = useState(1)
  const [recurrenceUnit, setRecurrenceUnit] = useState('month')
  const [recurrenceEnd, setRecurrenceEnd] = useState('')

  const [units, setUnits] = useState([])
  const [unitsLoading, setUnitsLoading] = useState(false)
  const [unitIds, setUnitIds] = useState([])
  const [unitMode, setUnitMode] = useState('shared')

  const [contractors, setContractors] = useState([])
  const [contractorsLoading, setContractorsLoading] = useState(false)
  const [selectedContractor, setSelectedContractor] = useState(null)

  const bpId = action?.buildingPassport?.id || null
  const hasTaskManagement = action?.buildingPassport?.hasTaskManagement
  const isOwner = action?.buildingPassport?.isOwner
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [requested, setRequested] = useState(false)

  // Pre-fill from action when opened
  useEffect(() => {
    if (action && open) {
      setTitle(action.title || '')
      setDeadline(action.dueDate || action.suggestedDueBy || '')
      setPriority(SEVERITY_TO_PRIORITY[action.severity] || 'medium')
      setAssignees([])
      setFormTemplate(null)
      setTaskDomainId('')
      setRecurrenceType('one_off')
      setRecurrenceInterval(1)
      setRecurrenceUnit('month')
      setRecurrenceEnd('')
      setSelectedContractor(null)
      setPoNumber('')
      setUserQuery('')
      // If the action was tagged against a single unit, pre-select it.
      // Otherwise leave empty so the user picks (or skips for BP-level work).
      setUnitIds(action.buildingUnitPassportId ? [action.buildingUnitPassportId] : [])
      setUnitMode('shared')
      setShowUpgrade(false)
      setRequested(false)
    }
  }, [action, open])

  // Load units for the BP. The list is small enough to render inline as
  // checkboxes without a search input — BPs rarely have hundreds of units.
  useEffect(() => {
    if (!open || !bpId) { setUnits([]); return }
    let cancelled = false
    setUnitsLoading(true)
    fetchBpUnits({ bpId })
      .then((res) => {
        if (cancelled) return
        const entries = res?.data?.entries ?? res?.data?.data ?? []
        setUnits(Array.isArray(entries) ? entries : [])
      })
      .catch(() => { if (!cancelled) setUnits([]) })
      .finally(() => { if (!cancelled) setUnitsLoading(false) })
    return () => { cancelled = true }
  }, [open, bpId])

  // Search assignable users (debounced) — scoped to this BP
  useEffect(() => {
    if (!open || !bpId) return
    let cancelled = false
    setUsersLoading(true)
    searchAssignableUsers({ search: debouncedUserQuery, buildingPassportId: bpId })
      .then(res => {
        if (cancelled) return
        setUserOptions(res?.data?.data || [])
      })
      .catch(() => { if (!cancelled) setUserOptions([]) })
      .finally(() => { if (!cancelled) setUsersLoading(false) })
    return () => { cancelled = true }
  }, [open, bpId, debouncedUserQuery])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    fetchTaskDomains()
      .then(res => { if (!cancelled) setDomains(res?.data?.data || res?.data || []) })
      .catch(() => { if (!cancelled) setDomains([]) })
    return () => { cancelled = true }
  }, [open])

  useEffect(() => {
    if (!open) { setContractors([]); setContractorsLoading(false); return }
    let cancelled = false
    setContractorsLoading(true)
    listContractors()
      .then((res) => {
        if (cancelled) return
        setContractors(res?.data?.data?.contractors || [])
      })
      .catch(() => { if (!cancelled) setContractors([]) })
      .finally(() => { if (!cancelled) setContractorsLoading(false) })
    return () => { cancelled = true }
  }, [open])

  const submit = async () => {
    if (hasTaskManagement === false) {
      setShowUpgrade(true)
      return
    }
    const payload = { id: action.id }
    if (title.trim() && title !== action.title) payload.titleOverride = title.trim()
    payload.deadline = deadline || null
    payload.priority = priority
    if (assignees.length > 0) payload.assigneeIds = assignees.map(u => u.id)
    if (formTemplate?.id) payload.formTemplateId = formTemplate.id
    if (taskDomainId) payload.taskDomainId = taskDomainId
    if (recurrenceType === 'repeating') {
      payload.recurrenceType = 'repeating'
      payload.recurrenceInterval = Number(recurrenceInterval) || 1
      payload.recurrenceUnit = recurrenceUnit
      if (recurrenceEnd) payload.recurrenceEndDate = recurrenceEnd
    }
    if (selectedContractor?.id) payload.contractorId = selectedContractor.id
    if (poNumber.trim()) payload.poNumber = poNumber.trim()
    if (unitIds.length > 0) {
      payload.unitIds = unitIds
      // unit_mode only matters when 2+ units are selected; with one unit the
      // backend coerces to 'single' anyway. Send for clarity.
      payload.unitMode = unitIds.length > 1 ? unitMode : 'single'
    }

    setSubmitError(null)
    try {
      await convertAsync(payload, dispatch)
      onConverted?.(action)
      onClose?.()
    } catch (e) {
      setSubmitError(firstErrorMessage(e, 'The task was not created. Your details are still here, so you can try again.'))
    }
  }

  const requestAccess = () => {
    if (!bpId || requesting || requested) return
    setRequesting(true)
    requestTaskManagementFeature(bpId)
      .then(() => {
        setRequested(true)
        toast.success(`Request sent. The team will be in touch about ${action?.buildingPassport?.name || 'this Building Passport'}.`)
      })
      .catch((e) => {
        const already = e?.response?.status === 422
        toast.error(already ? 'Task Management is already enabled for this Building Passport.' : 'Could not send the request. Please try again.')
      })
      .finally(() => setRequesting(false))
  }

  const toggleUnit = (unitId) => {
    setUnitIds((prev) => prev.includes(unitId)
      ? prev.filter((id) => id !== unitId)
      : [...prev, unitId])
  }
  const allUnitsSelected = units.length > 0 && unitIds.length === units.length

  const severityHint = useMemo(() => {
    if (!action?.severity) return null
    const mapped = SEVERITY_TO_PRIORITY[action.severity]
    if (!mapped || mapped === priority) return null
    return `Set to ${mapped.charAt(0).toUpperCase()}${mapped.slice(1)} because this remedial’s severity is ${severityMeta(action.severity).label}`
  }, [action, priority])

  if (!action) return null

  const popperSlot = zIndex ? { popper: { style: { zIndex: zIndex + 10 } } } : undefined

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
      sx={zIndex ? { zIndex } : undefined}
    >
      <DialogTitle sx={dialogTitleSx}>{showUpgrade ? 'Task Management needed' : 'Create task from remedial'}</DialogTitle>
      {showUpgrade ? (
        <>
          <DialogContent sx={dialogContentSx}>
            <UpgradeBox>
              <LockOutlinedIcon style={{ fontSize: 30, color: '#b45309' }} />
              <UpgradeTitle>You do not have Task Management enabled</UpgradeTitle>
              <UpgradeText>
                {isOwner
                  ? `Task Management isn’t switched on for this Building Passport yet. As an ${ROLE_LABELS.owner} of this Building Passport you can request it below and our team will be in touch.`
                  : `Only an ${ROLE_LABELS.owner} of this Building Passport can request Task Management. Please ask one of them to switch it on.`}
              </UpgradeText>
              {action?.buildingPassport?.name ? <UpgradeBp>{action.buildingPassport.name}</UpgradeBp> : null}
            </UpgradeBox>
          </DialogContent>
          <DialogActions sx={dialogActionsSx}>
            <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>Close</Button>
            {isOwner ? (
              <Button
                variant="contained"
                onClick={requestAccess}
                disabled={requesting || requested}
                sx={submitButtonSx}
              >
                {requested ? 'Request sent ✓' : requesting ? 'Sending…' : 'Request feature'}
              </Button>
            ) : null}
          </DialogActions>
        </>
      ) : (
        <>
      <DialogContent sx={dialogContentSx}>
        <TextField
          label="Task title"
          required
          fullWidth
          size="small"
          value={title}
          onChange={e => setTitle(e.target.value)}
          data-tour-id='convertToTask.title'
        />

        <TextField
          label="Priority"
          select
          fullWidth
          size="small"
          value={priority}
          onChange={e => setPriority(e.target.value)}
          helperText={severityHint || ' '}
          SelectProps={zIndex ? { MenuProps: { sx: { zIndex: zIndex + 10 } } } : undefined}
        >
          {PRIORITIES.map(p => (
            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Task domain"
          select
          fullWidth
          size="small"
          value={taskDomainId}
          onChange={e => setTaskDomainId(e.target.value)}
          helperText="Remedials and incidents don't carry a task domain, so choose one here."
          SelectProps={zIndex ? { MenuProps: { sx: { zIndex: zIndex + 10 } } } : undefined}
        >
          <MenuItem value="">No specific domain</MenuItem>
          {domains.map(d => (
            <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Deadline"
          type="date"
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          value={deadline}
          onChange={e => setDeadline(e.target.value)}
        />

        <TextField
          label="Repeat"
          select
          fullWidth
          size="small"
          value={recurrenceType}
          onChange={e => setRecurrenceType(e.target.value)}
          helperText={recurrenceType === 'repeating'
            ? 'A new task is created automatically each cycle.'
            : 'A single one-off task. It won’t repeat on a schedule.'}
          SelectProps={zIndex ? { MenuProps: { sx: { zIndex: zIndex + 10 } } } : undefined}
        >
          <MenuItem value="one_off">One-off</MenuItem>
          <MenuItem value="repeating">Repeats</MenuItem>
        </TextField>

        {recurrenceType === 'repeating' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <TextField
              label="Every"
              type="number"
              size="small"
              inputProps={{ min: 1 }}
              value={recurrenceInterval}
              onChange={e => setRecurrenceInterval(e.target.value)}
              sx={{ width: 90 }}
            />
            <TextField
              label="Unit"
              select
              size="small"
              value={recurrenceUnit}
              onChange={e => setRecurrenceUnit(e.target.value)}
              sx={{ flex: 1 }}
              SelectProps={zIndex ? { MenuProps: { sx: { zIndex: zIndex + 10 } } } : undefined}
            >
              <MenuItem value="week">Week(s)</MenuItem>
              <MenuItem value="month">Month(s)</MenuItem>
              <MenuItem value="year">Year(s)</MenuItem>
            </TextField>
            <TextField
              label="Ends (optional)"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={recurrenceEnd}
              onChange={e => setRecurrenceEnd(e.target.value)}
              sx={{ flex: 1.2 }}
            />
          </div>
        )}

        <TextField
          label="Purchase order number"
          fullWidth
          size="small"
          value={poNumber}
          onChange={e => setPoNumber(e.target.value)}
          placeholder="e.g. PO-2026-001234"
          helperText="For the contractor and your approver, in your own words"
        />

        <Autocomplete
          multiple
          slotProps={popperSlot}
          size="small"
          options={userOptions}
          value={assignees}
          onChange={(_, v) => setAssignees(v)}
          inputValue={userQuery}
          onInputChange={(_, v, reason) => {
            if (reason !== 'reset') setUserQuery(v)
          }}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          loading={usersLoading}
          noOptionsText={bpId ? 'No matching users for this Building Passport' : 'Building Passport unavailable'}
          renderTags={(value, getTagProps) =>
            value.map((u, idx) => (
              <Chip
                size="small"
                label={userLabel(u)}
                {...getTagProps({ index: idx })}
                key={u.id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assignees"
              placeholder={assignees.length ? '' : 'Search users…'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          slotProps={popperSlot}
          size="small"
          options={contractors}
          value={selectedContractor}
          onChange={(_, v) => setSelectedContractor(v)}
          getOptionLabel={(c) => (c ? (c.company ? `${c.name} (${c.company})` : c.name) : '')}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          loading={contractorsLoading}
          noOptionsText="No contractors available"
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <ContractorOption>
                <ContractorOptionName>{option.name}</ContractorOptionName>
                <ContractorOptionCompany>{option.company}</ContractorOptionCompany>
                {option.assignmentBlocked && (
                  <ContractorBlocked>
                    Assignment blocked: {option.blockedReasons?.[0]}
                  </ContractorBlocked>
                )}
              </ContractorOption>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Responsible contractor (optional)"
              placeholder="Assign a contractor"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {contractorsLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <UnitsBlock>
          <UnitsHead>
            <UnitsHeadTitle>
              <MeetingRoomOutlinedIcon style={{ fontSize: 16, color: '#f59e0b' }} />
              Unit Passports (optional)
            </UnitsHeadTitle>
            <UnitsHeadHint>
              Pick one to keep the task on a single Unit Passport, or several to either share
              one task across them or fan out into separate tasks per Unit Passport.
            </UnitsHeadHint>
          </UnitsHead>
          {unitsLoading ? (
            <UnitsLoading><CircularProgress size={16} /></UnitsLoading>
          ) : units.length === 0 ? (
            <UnitsEmpty>This Building Passport has no Unit Passports, so the task will be filed against the Building Passport itself.</UnitsEmpty>
          ) : (
            <>
              <UnitsList>
                <UnitRow>
                  <input
                    type="checkbox"
                    id="conv-unit-all"
                    checked={allUnitsSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = unitIds.length > 0 && !allUnitsSelected
                    }}
                    onChange={() => {
                      setUnitIds(allUnitsSelected ? [] : units.map((u) => u.id))
                    }}
                  />
                  <UnitRowLabel htmlFor="conv-unit-all" $bold>
                    Select all ({units.length})
                  </UnitRowLabel>
                </UnitRow>
                <UnitsDivider />
                {units.map((unit) => {
                  const checked = unitIds.includes(unit.id)
                  return (
                    <UnitRow key={unit.id}>
                      <input
                        type="checkbox"
                        id={`conv-unit-${unit.id}`}
                        checked={checked}
                        onChange={() => toggleUnit(unit.id)}
                      />
                      <UnitRowLabel htmlFor={`conv-unit-${unit.id}`}>
                        {unit.name || unit.title || unit.id}
                      </UnitRowLabel>
                    </UnitRow>
                  )
                })}
              </UnitsList>

              {unitIds.length > 1 && (
                <UnitMode>
                  <UnitModeLabel>How should the task be created?</UnitModeLabel>
                  <UnitModeOption>
                    <input
                      type="radio"
                      id="conv-unit-mode-shared"
                      name="conv-unit-mode"
                      checked={unitMode === 'shared'}
                      onChange={() => setUnitMode('shared')}
                    />
                    <label htmlFor="conv-unit-mode-shared">
                      <strong>One shared task</strong> across all {unitIds.length} Unit Passports
                    </label>
                  </UnitModeOption>
                  <UnitModeOption>
                    <input
                      type="radio"
                      id="conv-unit-mode-fan"
                      name="conv-unit-mode"
                      checked={unitMode === 'fan_out'}
                      onChange={() => setUnitMode('fan_out')}
                    />
                    <label htmlFor="conv-unit-mode-fan">
                      <strong>Separate task per Unit Passport</strong> ({unitIds.length} tasks total)
                    </label>
                  </UnitModeOption>
                </UnitMode>
              )}
            </>
          )}
        </UnitsBlock>

        <FormFieldBlock>
          <FormFieldLabel>Form (optional)</FormFieldLabel>
          <FormFieldHint>
            A form is the list of questions the person doing this work will answer. Leave it as
            &ldquo;no form&rdquo; if they just need to mark the work as done.
          </FormFieldHint>
          <FormTemplateField
            value={formTemplate?.id || ''}
            template={formTemplate}
            onChange={setFormTemplate}
            buildingPassportId={bpId || undefined}
            allowNone
            noneLabel='No form needed'
            noneDescription='The person doing the work will just mark it as done.'
          />
        </FormFieldBlock>
      </DialogContent>
      {submitError && <SubmitError role='alert'>{submitError}</SubmitError>}
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || !title.trim()}
          sx={submitButtonSx}
          data-tour-id='convertToTask.submit'
        >
          Create task
        </Button>
      </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default ConvertToTaskDialog

const FormFieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FormFieldLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c22;
`

const FormFieldHint = styled.span`
  font-size: 14px;
  line-height: 1.5;
  color: #62626d;
`

const UpgradeBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 16px 8px 4px;
`

const UpgradeTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0C1229;
`

const UpgradeText = styled.div`
  font-size: 13.5px;
  color: #475569;
  line-height: 1.55;
  max-width: 380px;
`

const UpgradeBp = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 999px;
`

const ContractorOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const ContractorOptionName = styled.span`
  font-size: 13px;
  color: #0f172a;
`

const ContractorOptionCompany = styled.span`
  font-size: 11px;
  color: #64748b;
`

const ContractorBlocked = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #b00020;
`

const UnitsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafbfc;
`

const UnitsHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const UnitsHeadTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
`

const UnitsHeadHint = styled.div`
  font-size: 11px;
  color: #64748b;
  line-height: 1.5;
`

const UnitsLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
`

const UnitsEmpty = styled.div`
  font-size: 12px;
  color: #64748b;
  padding: 8px 4px;
`

const UnitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 180px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 8px;
`

const UnitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 2px;
`

const UnitRowLabel = styled.label`
  font-size: 12px;
  color: #0f172a;
  cursor: pointer;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
`

const UnitsDivider = styled.div`
  border-bottom: 1px solid #f1f5f9;
  margin: 2px 0;
`

const UnitMode = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
`

const UnitModeLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 2px;
`

const UnitModeOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #0f172a;
  label { cursor: pointer; }
  strong { font-weight: 600; }
`
